import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  calculateAnchoredScrollTop,
  calculateVisibleRange,
  isNearLatest,
  TASK_LOG_ROW_HEIGHT,
  type DisplayRow,
} from "../model/task-log-view";

export function useVirtualTaskLogs(rows: DisplayRow[], logCount: number) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const previousRowsRef = useRef<DisplayRow[]>([]);
  const [isFollowing, setIsFollowing] = useState(true);
  const [countWhenUnfollowed, setCountWhenUnfollowed] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(400);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const observer = new ResizeObserver((entries) => {
      setViewportHeight(entries[0]?.contentRect.height ?? viewport.clientHeight);
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const previousRows = previousRowsRef.current;

    if (viewport && isFollowing) {
      viewport.scrollTop = 0;
    } else if (viewport && previousRows.length > 0) {
      const anchoredScrollTop = calculateAnchoredScrollTop(
        previousRows,
        rows,
        viewport.scrollTop,
      );
      if (anchoredScrollTop !== undefined) viewport.scrollTop = anchoredScrollTop;
    }

    previousRowsRef.current = rows;
  }, [isFollowing, rows]);

  const pauseFollowing = useCallback(() => {
    setIsFollowing(false);
    setCountWhenUnfollowed(logCount);
  }, [logCount]);

  const scrollToLatest = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    setIsFollowing(true);
    setCountWhenUnfollowed(logCount);
    viewport.scrollTop = 0;
  }, [logCount]);

  const handleScroll = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    setScrollTop(viewport.scrollTop);
    const nearLatest = isNearLatest(viewport.scrollTop);
    if (!nearLatest && isFollowing) {
      setIsFollowing(false);
      setCountWhenUnfollowed(logCount);
    } else if (nearLatest && !isFollowing) {
      setIsFollowing(true);
      setCountWhenUnfollowed(logCount);
    }
  }, [isFollowing, logCount]);

  const { startIndex, endIndex } = calculateVisibleRange(
    rows.length,
    scrollTop,
    viewportHeight,
  );

  return {
    handleScroll,
    isFollowing,
    newLogCount: isFollowing ? 0 : Math.max(0, logCount - countWhenUnfollowed),
    pauseFollowing,
    scrollToLatest,
    startIndex,
    totalHeight: rows.length * TASK_LOG_ROW_HEIGHT,
    viewportRef,
    visibleRows: rows.slice(startIndex, endIndex),
  };
}
